import '@iotize/tap/service/impl/interface';

import { Tap } from '@iotize/tap';
import { Observable, Subject } from 'rxjs';
import { tap as rxTap } from 'rxjs/operators';

import { debug } from './debug';

export const DEFAULT_KEEP_ALIVE_PERIOD_MS = 5000;

const TAG = 'KeepAliveEngine';
// Create tap extension to provide datalogger class

export type KeepAliveEvent =
  | KeepAliveEventError
  | KeepAliveEventTick
  | KeepAliveEventScheduleTick
  | { type: 'start' }
  | { type: 'stop' };

interface KeepAliveEventError {
  type: 'error';
  error: Error;
}

interface KeepAliveEventTick {
  type: 'tick';
}
interface KeepAliveEventScheduleTick {
  type: 'rescheduleKeepAlive';
  timeout: number;
}

export interface KeepAliveOptions {
  period: number;
}

export class KeepAliveEngine {
  private timeoutId?: any; // NodeJS.Timeout;
  private _lastResponseTime = 0;
  private _isStopped = true;
  private _frameCounter = 0;

  /**
   * Keep alive request count
   */
  get counter() {
    return this._frameCounter;
  }

  /**
   * returns event observable
   */
  get events(): Observable<KeepAliveEvent> {
    return this._events.asObservable();
  }

  private _events: Subject<KeepAliveEvent> = new Subject();

  /**
   * Change keep alive period
   */
  set period(p: number) {
    if (typeof p !== 'number') {
      p = 0;
    }
    this._options.period = p;
    if (this._options.period <= 0) {
      this.clearTimeout();
    } else if (!this.timeoutId && this.running) {
      this._setupNextTimeout(0);
    }
  }

  get running(): boolean {
    return !this._isStopped;
  }

  get options(): Readonly<KeepAliveOptions> {
    return this._options;
  }

  constructor(
    private tap: Tap,
    private _options: KeepAliveOptions,
  ) {
    debug(TAG, 'new KeepAliveEngine()');
    this.tap.client.addInterceptor((req, handle) => {
      return handle.handle(req).pipe(
        rxTap({
          next: () => {
            debug(
              TAG,
              'update last response time',
              this._lastResponseTime,
              '=>',
              new Date().getTime(),
            );
            this._lastResponseTime = new Date().getTime();
          },
        }),
      );
    });
  }

  start() {
    this._isStopped = false;
    if (!this.timeoutId && this.options.period > 0) {
      this._setupNextTimeout(0);
    }
    this._events.next({
      type: 'start',
    });
  }

  stop() {
    this._isStopped = true;
    this.clearTimeout();
    this._events.next({
      type: 'stop',
    });
  }

  private clearTimeout() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
  }

  private _setupNextTimeout(timeoutMs: number) {
    if (this._isStopped) {
      return;
    }
    this._events.next({
      type: 'rescheduleKeepAlive',
      timeout: timeoutMs,
    });
    this.timeoutId = setTimeout(async () => {
      if (this.tap.isConnected()) {
        const remainingTime =
          this._options.period - elapsedTime(this._lastResponseTime);
        if (remainingTime > 0) {
          debug(TAG, 'Reschedule keep alive in ', remainingTime);
          this._setupNextTimeout(remainingTime);
        } else {
          debug(TAG, 'Running keep alive');
          this._frameCounter++;
          try {
            this._events.next({
              type: 'tick',
            });
            await this.tap.service.interface.keepAlive();
          } catch (err) {
            this._events.next({
              type: 'error',
              error: err as Error,
            });
          }
          if (this._options.period > 0) {
            this._setupNextTimeout(this._options.period);
          }
        }
      }
    }, timeoutMs);
  }
}

function elapsedTime(time: number) {
  return new Date().getTime() - time;
}
